// appRoutes.tsx
import React from "react";
import { Navigate } from "react-router-dom";
import DashboardPageLayout from "../pages/dashboard/DashboardPageLayout";
import Submission from "../pages/Submissions/SubmissionList";
import Inbox from "../pages/Inbox/Inbox";
import ComponentPageLayout from "../pages/component/ComponentPageLayout";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import CreateForm from "../pages/component/CreateForm";
import InboxIcon from "@mui/icons-material/Inbox";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NotesIcon from "@mui/icons-material/Notes";
import PublishedForm from "../pages/component/PublishedForm";
import LoginComponent from "../pages/auth/login";
import TrashForms from "../pages/component/TrashFroms";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Documents from "../pages/document";
import AddDocument from "../pages/document/AddDocument";
import Reports from "../pages/dashboard/Reports";
import RoleBasedRoute from "./RoleBasedRoute";
import RouteType from "./config";
import { hasAccess } from "../utils/checkRole";
import ConditionalNavigate from "../utils/conditionalNav";
import FolderView from "../pages/document/folderView";
import HelpResources from "../pages/resources/helpResources";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import FederatedReports from "../pages/dashboard/federatedreport";

const appRoutes = (userRole: string): RouteType[] => [
  {
    index: true,
    element: <ConditionalNavigate />,
    state: "home",
  },
  {
    path: "/login",
    element: <LoginComponent />,
    state: "LoginComponent",
  },
  // Conditionally render routes based on userRole
  ...(userRole
    ? [
      {
        path: "/dashboard",
        element: (
          <RoleBasedRoute
            roles={["superadmin", "admin", "manager","admintest"]}
            userRole={userRole}
          >
            <DashboardPageLayout />
          </RoleBasedRoute>
        ),
        state: "dashboard",
        sidebarProps: hasAccess(["superadmin", "admin", "manager","admintest"], userRole)
          ? {
            displayText: "Dashboard",
            icon: <DashboardOutlinedIcon />,
          }
          : undefined,
        child: [
          {
            path: "/dashboard/reports",
            element: (
              <RoleBasedRoute
                roles={["superadmin", "admin", "manager","admintest"]}
                userRole={userRole}
              >
                <Reports />
              </RoleBasedRoute>
            ),
            state: "dashboard.reports",
            sidebarProps: hasAccess(
              ["superadmin", "admin", "manager","admintest"],
              userRole
            )
              ? {
                displayText: "Reports",
                icon: "",
              }
              : undefined,
          },
                    {
            path: "/dashboard/federatedReport",
            element: (
              <RoleBasedRoute
                roles={["superadmin"]}
                userRole={userRole}
              >
                <FederatedReports />
              </RoleBasedRoute>
            ),
            state: "dashboard.federatedReport",
            sidebarProps: hasAccess(
              ["superadmin"],
              userRole
            )
              ? {
                displayText: "Federated Reports",
                icon: "",
              }
              : undefined,
          },
        ],
      },
      {
        path: "/form",
        element: (
          <RoleBasedRoute roles={["superadmin","admintest", "admin"]} userRole={userRole}>
            <ComponentPageLayout />
          </RoleBasedRoute>
        ),
        state: "component",
        sidebarProps: hasAccess(["superadmin","admintest", "admin"], userRole)
          ? {
            displayText: "Forms",
            icon: <AppsOutlinedIcon />,
          }
          : undefined,
        child: [
          {
            path: "/form/publishedforms",
            element: (
              <RoleBasedRoute roles={["superadmin","admintest", "admin"]} userRole={userRole}>
                <PublishedForm draft={false} />
              </RoleBasedRoute>
            ),
            state: "Form.PublishedForm",
            sidebarProps: hasAccess(["superadmin","admintest", "admin"], userRole)
              ? {
                displayText: "Published Forms",
                icon: <NotesIcon />,
              }
              : undefined,
          },
          {
            path: "/form/draftforms",
            element: (
              <RoleBasedRoute roles={["superadmin","admintest", "admin"]} userRole={userRole}>
                <PublishedForm draft={true} />
              </RoleBasedRoute>
            ),
            state: "Form.DraftForm",
            sidebarProps: hasAccess(["superadmin","admintest", "admin"], userRole)
              ? {
                displayText: "Draft Forms",
                icon: <NotesIcon />,
              }
              : undefined,
          },
          {
            path: "/form/createform",
            element: (
              <RoleBasedRoute roles={["superadmin","admintest", "admin"]} userRole={userRole}>
                <CreateForm />
              </RoleBasedRoute>
            ),
            state: "Form.CreateForm",
            sidebarProps: hasAccess(["superadmin","admintest", "admin"], userRole)
              ? {
                displayText: "Create New Form",
                icon: <EditNoteIcon />,
              }
              : undefined,
          },
          {
            path: "/form/trashforms",
            element: (
              <RoleBasedRoute roles={["superadmin","admintest", "admin"]} userRole={userRole}>
                <TrashForms />
              </RoleBasedRoute>
            ),
            state: "Form.TrashForm",
            sidebarProps: hasAccess(["superadmin","admintest", "admin"], userRole)
              ? {
                displayText: "Trash Forms",
                icon: <DeleteOutlineIcon />,
              }
              : undefined,
          },
        ],
      },
      {
        path: "/submissions",
        element: (
          <RoleBasedRoute
            roles={["superadmin", "admin", "manager","admintest"]}
            userRole={userRole}
          >
            <Submission />
          </RoleBasedRoute>
        ),
        state: "submission",
        sidebarProps: hasAccess(["superadmin", "admin", "manager","admintest"], userRole)
          ? {
            displayText: "Submissions",
            icon: <FormatListBulletedOutlinedIcon />,
          }
          : undefined,
      },
      {
        path: "/inbox",
        element: (
          <RoleBasedRoute roles={["superadmin"]} userRole={userRole}>
            <Inbox />
          </RoleBasedRoute>
        ),
        state: "inbox",
        sidebarProps: hasAccess(["superadmin"], userRole)
          ? {
            displayText: "Inbox",
            icon: <InboxIcon />,
          }
          : undefined,
      },
      {
        path: "/documents",
        element: (
          <RoleBasedRoute roles={["superadmin", "admin","admintest"]} userRole={userRole}>
            <Documents />
          </RoleBasedRoute>
        ),
        state: "documents",
        sidebarProps: hasAccess(["superadmin", "admin","admintest"], userRole)
          ? {
            displayText: "Documents",
            icon: <ArticleOutlinedIcon />,
          }
          : undefined,
      },
      {
        path: "/documents/add",
        element: (
          <RoleBasedRoute roles={["superadmin", "admin","admintest"]} userRole={userRole}>
            <AddDocument folderId={""} folderSysNum={""} folderAllSysNum={""}/>
          </RoleBasedRoute>
        ),
        state: "addDocuments",
      },
      {
        path: "/folder/:folderId",
        element: (
          <RoleBasedRoute roles={["superadmin", "admin","admintest"]} userRole={userRole}>
            <FolderView />
          </RoleBasedRoute>
        ),
        state: "documents.folder",
      },
      {
        path: "/helpResources",
        element: (
          <RoleBasedRoute
            roles={["superadmin", "admin","admintest"]}
            userRole={userRole}
          >
            <HelpResources />
          </RoleBasedRoute>
        ),
        state: "Help & Resources",
        sidebarProps: hasAccess(["superadmin", "admin","admintest"], userRole)
          ? {
            displayText: "Help & Resources",
            icon: <QuestionMarkIcon />,
          }
          : undefined,
      },
    ]
    : []), 
];

export default appRoutes;
